import { createContext, useContext, useState, useEffect } from 'react';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { sha512 } from 'js-sha512';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true); // prevents flash of login screen

  // Persist login across page refresh
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Fetch role + name from Firestore
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (userDoc.exists()) {
          setUser({ uid: firebaseUser.uid, ...userDoc.data() });
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return unsubscribe; // cleanup on unmount
  }, []);

  const login = async (email, password) => {
    try {
      // SHA-512 pre-hash per system security specification
      const hashedPassword = sha512(password);
      await signInWithEmailAndPassword(auth, email, hashedPassword);
      // onAuthStateChanged above will set user + role from Firestore
      return { success: true };
    } catch (error) {
      const messages = {
        'auth/user-not-found':    'Account not found.',
        'auth/wrong-password':    'Incorrect password.',
        'auth/invalid-email':     'Invalid email address.',
        'auth/invalid-credential':'Invalid email or password.',
        'auth/too-many-requests': 'Too many attempts. Try again later.',
      };
      return {
        success: false,
        message: messages[error.code] || 'Login failed. Please try again.',
      };
    }
  };

  const logout = () => signOut(auth);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}